// frontend/src/components/portfolio/BlockEditModal.tsx
import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  AppBar,
  Toolbar,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Image as ImageIcon,
  Flag as FlagIcon,
  BarChart as BarChartIcon,
  Work as WorkIcon,
  CardGiftcard as CardGiftcardIcon,
  PhotoLibrary as PhotoLibraryIcon,
  FormatQuote as FormatQuoteIcon,
  TextFields as TextFieldsIcon,
  Email as EmailIcon,
  Group as GroupIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  Visibility as VisibilityIcon,
  Timeline as TimelineIcon,
  PlayCircle as PlayCircleIcon,
  Link as LinkIcon,
  AccountTree as AccountTreeIcon,
  LibraryMusic as ArtistSearchIcon
} from '@mui/icons-material';
import { BlockData } from '../../types/Portfolio';
import { getBlockByType } from '../../utils/blockRegistry';
import BlockFormBuilder from './BlockFormBuilder';

interface BlockEditModalProps {
  open: boolean;
  block: BlockData | null;
  onClose: () => void;
  onSave: (blockId: string, data: any) => void;
  onDelete: (blockId: string) => void;
  onAutoSave?: () => Promise<void>; // NEW: Auto-save trigger
}

const iconMap: Record<string, React.ReactElement> = {
  hero: <ImageIcon />,
  mission_vision: <FlagIcon />,
  metrics: <BarChartIcon />,
  case_studies: <WorkIcon />,
  offerings: <CardGiftcardIcon />,
  gallery: <PhotoLibraryIcon />,
  testimonials: <FormatQuoteIcon />,
  rich_text: <TextFieldsIcon />,
  contact_form: <EmailIcon />,
  team: <GroupIcon />,
  past_impact: <TimelineIcon />,
  audience_demographics: <PeopleIcon />,
  media_reach: <CampaignIcon />,
  brand_visibility: <VisibilityIcon />,
  video_showcase: <PlayCircleIcon />,
  links: <LinkIcon />,
  programme_initiatives: <AccountTreeIcon />,
  artist: <ArtistSearchIcon />
};

const BlockEditModal: React.FC<BlockEditModalProps> = ({
  open,
  block,
  onClose,
  onSave,
  onDelete,
  onAutoSave
}) => {
  const [formData, setFormData] = React.useState<any>(null);
  const [saving, setSaving] = React.useState(false);

  // Initialize form data when block changes
  React.useEffect(() => {
    if (block) {
      setFormData(block.data);
    }
  }, [block]);

  if (!block) {
    return null;
  }

  const blockConfig = getBlockByType(block.type);
  const blockLabel = blockConfig?.label || block.type;
  const blockIcon = iconMap[block.type] || <ImageIcon />;

  const handleFormChange = (data: any) => {
    console.log('BlockEditModal handleFormChange called with:', data);
    setFormData(data);
  };

  const handleSave = async () => {
    console.log('BlockEditModal handleSave - formData:', formData);
    if (!formData) return;

    try {
      setSaving(true);
      
      // Update block data in memory
      onSave(block.id, formData);
      console.log('BlockEditModal called onSave with blockId:', block.id, 'data:', formData);
      
      // Auto-save to backend
      if (onAutoSave) {
        await onAutoSave();
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving block:', error);
      alert('Failed to save block changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete this ${blockLabel} block? This action cannot be undone.`)) {
      onDelete(block.id);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: '#2C3E50'
        }
      }}
    >
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: '#34495E',
          borderBottom: '2px solid #3A9188'
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Box sx={{ color: '#3A9188', display: 'flex' }}>
              {blockIcon}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff' }}>
              Edit {blockLabel}
            </Typography>
            {saving && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                <CircularProgress size={16} sx={{ color: '#3A9188' }} />
                <Typography variant="body2" sx={{ color: '#3A9188' }}>
                  Saving...
                </Typography>
              </Box>
            )}
          </Box>

          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
            sx={{ mr: 2 }}
            disabled={saving}
          >
            Delete Block
          </Button>

          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: '#3A9188',
              '&:hover': {
                bgcolor: '#2ECC71'
              },
              mr: 2
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>

          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Content Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 4,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#3A9188',
            borderRadius: '4px'
          }
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          {blockConfig && formData ? (
            <BlockFormBuilder
              blockType={block.type}
              blockData={formData}
              editFields={blockConfig.editFields}
              onChange={handleFormChange}
            />
          ) : (
            <Typography sx={{ color: '#E57373' }}>
              Block configuration not found for type: {block.type}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          bgcolor: '#34495E',
          borderTop: '2px solid #3A9188',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={saving}
          sx={{
            borderColor: '#A4B0BE',
            color: '#A4B0BE',
            '&:hover': {
              borderColor: '#ffffff',
              color: '#ffffff'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            bgcolor: '#3A9188',
            '&:hover': {
              bgcolor: '#2ECC71'
            }
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default BlockEditModal;